/**
 * The intake editor — one goods-received document.
 *
 * Replaces PurchaseStockView, whose five tabs rendered one working screen
 * and whose supplier, date, invoice and order-type fields were never stored:
 * its Formik onSubmit was empty. Charges were hard-coded to 0.
 *
 * What changed, beyond it working:
 *  - A line is a PRODUCT or an INGREDIENT. One delivery is often both.
 *  - Quantities are decimals. The legacy mapStockRecord did parseInt, so
 *    2.5 kg of rice saved as 2.
 *  - Every total is computed by the server. The figures here are a preview
 *    while typing; what gets stored is whatever comes back.
 *  - A posted document is read-only. The ledger is append-only, so the only
 *    correction is a void plus a new document.
 *
 * GRN additions (docs/feature-stock-grn-variants.md §5.1): delivery note
 * number, received by, attachments, a searchable supplier picker with inline
 * add, repeat-last-delivery prefill, a Variant select on lines for products
 * tracked per variant, and "Receive now" — create and post in one step.
 *
 * See docs/feature-stock-intake-and-suppliers.md §7.1.
 */
import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {Actions} from '../../../internals/app/Actions';
import StockIntakeRepository, {
  estimateTotals,
  newIntakeId,
  type IntakeCharge,
  type IntakeLine,
  type LineKind,
  type StockIntake,
} from '../../../internals/repository/StockIntakeRepository';
import SupplierRepository, {type Supplier} from '../../../internals/repository/SupplierRepository';
import {stepFor, type StockItem} from '../../../internals/repository/StockItemRepository';
import SupplierCombobox from './SupplierCombobox';
import SearchSelect from '../../../components/ui/SearchSelect';
import AddProduct from '../../product/views/AddProduct';
import IntakeAttachments, {type AttachmentItem, toPendingItems} from './IntakeAttachments';
import {
  applyPrefill,
  isFilled,
  postedViaLabel,
  projectedOnHand,
  unitLabel,
  variantProblems,
  variantsOf,
} from '../intakeEditor';

const inputCls = 'nl-input';

/**
 * A number box with its unit or currency inside the border, so the line's
 * controls are all one 40px row. The wrapper carries the .nl-input look.
 */
const AffixInput = ({
  prefix,
  suffix,
  disabled,
  ...input
}: React.InputHTMLAttributes<HTMLInputElement> & {prefix?: string; suffix?: string}) => (
  <div
    className={`flex h-10 items-center rounded-[10px] border border-borderc2 px-3 transition-shadow focus-within:border-brand focus-within:shadow-[0_0_0_3px_var(--brandSoft)] ${
      disabled ? 'bg-surface2 text-text3' : 'bg-surface'
    }`}
  >
    {prefix && <span className="mr-2 shrink-0 text-xs text-text3">{prefix}</span>}
    <input
      {...input}
      disabled={disabled}
      className="w-full min-w-0 bg-transparent text-right text-sm tabular-nums text-text1 outline-none disabled:cursor-not-allowed disabled:text-text3"
    />
    {suffix && <span className="ml-2 shrink-0 text-xs text-text3">{suffix}</span>}
  </div>
);
const selectCls = 'nl-select';
const labelCls = 'nl-label';

const fmt = (n: number, currency?: string) =>
  `${currency || ''} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();

const newLineId = () => `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const blankLine = (kind: LineKind = 'ingredient'): IntakeLine =>
  kind === 'product'
    ? {lineId: newLineId(), kind, productId: null, stockId: null, variantId: null, qty: 0, unitCost: 0}
    : {lineId: newLineId(), kind, stockId: '', qty: 0, unitCost: 0};

const todayISO = () => new Date().toISOString().slice(0, 10);

const StockIntakeEditorView = (props: any) => {
  // Post and void go through the repository rather than a dispatched action:
  // this screen needs the RESPONSE (which line is now at what level, which
  // line can no longer be received), and the promise middleware resolves to
  // undefined. The store is refreshed with fetchIntake afterwards.
  const {
    selectedIntake,
    suppliers,
    ingredients,
    products,
    selectedShop,
    stockError,
    fetchIntake,
    fetchSuppliers,
    fetchIngredients,
    fetchProducts,
    clearSelectedIntake,
  } = props;

  const shopId = selectedShop?.shopId;
  const currency = selectedShop?.currency;
  const {intakeId} = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = intakeId === 'new';

  const [supplierId, setSupplierId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [deliveryNoteNo, setDeliveryNoteNo] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  /** The supplier's last delivery, offered as a prefill. */
  const [prefillOffer, setPrefillOffer] = useState<IntakeLine[] | null>(null);
  /** Lines waiting for "append to what you entered?" */
  const [prefillPending, setPrefillPending] = useState<IntakeLine[] | null>(null);
  /** Which confirmation is open. */
  const [confirming, setConfirming] = useState<'post' | 'receive' | null>(null);
  /**
   * The id a NEW document is received under. Made once and kept, so a retry
   * after a lost response hits the same document instead of receiving twice.
   */
  const [receiveId] = useState(() => newIntakeId());
  const [receivedAt, setReceivedAt] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<IntakeLine[]>([blankLine()]);
  /**
   * A product being created from a line's picker: the product editor opens
   * over the intake with the typed name, and the new product is put on that
   * line when it is saved.
   */
  const [newProduct, setNewProduct] = useState<{lineId: string; name: string} | null>(null);
  const newProductDefaults = useMemo(
    () => (newProduct ? {productName: newProduct.name, stockMode: 'unit'} : undefined),
    [newProduct],
  );
  const [charges, setCharges] = useState<IntakeCharge[]>([]);
  const [discountType, setDiscountType] = useState<'fixed' | 'percent'>('fixed');
  const [discountValue, setDiscountValue] = useState(0);

  const [problems, setProblems] = useState<{lineId: string | null; error: string}[]>([]);
  const [notice, setNotice] = useState<{kind: 'ok' | 'err'; text: string} | null>(null);
  const [busy, setBusy] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  /** What each line is worth after a post, keyed by lineId. */
  const [postedLevels, setPostedLevels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!shopId) return;
    fetchSuppliers(shopId);
    fetchIngredients(shopId);
    // Product lines only accept unit mode, but the whole catalogue is needed
    // to filter it.
    fetchProducts(shopId, 500, null, true);
  }, [shopId, fetchSuppliers, fetchIngredients, fetchProducts]);

  useEffect(() => {
    if (shopId && intakeId && !isNew) fetchIntake(shopId, intakeId);
    return () => clearSelectedIntake();
  }, [shopId, intakeId, isNew, fetchIntake, clearSelectedIntake]);

  /**
   * "Received" on a Stock Levels row lands here with ?prefill=kind:id, or
   * product:id:variantId for one variant. The line is seeded with the item
   * but NO quantity — the server refuses a line without one, so there is
   * nothing to save until a real number is entered.
   *
   * A Suppliers row lands here with ?supplierId=, preselecting the supplier
   * (and offering its last delivery).
   */
  useEffect(() => {
    if (!isNew) return;
    const preselected = searchParams.get('supplierId');
    if (preselected) setSupplierId(preselected);
    const prefill = searchParams.get('prefill');
    if (!prefill) return;
    const [kind, id, variantId] = prefill.split(':');
    if (kind !== 'product' && kind !== 'ingredient') return;
    setLines([
      {
        ...blankLine(),
        kind: kind as LineKind,
        ...(kind === 'product' ? {productId: id, variantId: variantId || null} : {stockId: id}),
      },
    ]);
  }, [isNew, searchParams]);

  /** Load a fetched document into the form. */
  useEffect(() => {
    const doc = selectedIntake as StockIntake | null;
    if (!doc || isNew || doc.intakeId !== intakeId) return;
    setSupplierId(doc.supplierId ?? '');
    setInvoiceNo(doc.invoiceNo ?? '');
    setDeliveryNoteNo(doc.deliveryNoteNo ?? '');
    setReceivedBy(doc.receivedBy ?? '');
    // Files still uploading or failed on screen are kept; stored ones come
    // from the document, which carries freshly signed URLs.
    setAttachments((items) => [
      ...(doc.attachments ?? []).map((attachment) => ({state: 'stored' as const, attachment})),
      ...items.filter((i) => i.state !== 'stored'),
    ]);
    setReceivedAt((doc.receivedAt ?? '').slice(0, 10) || todayISO());
    setNotes(doc.notes ?? '');
    setLines(doc.lines?.length ? doc.lines : [blankLine()]);
    setCharges(doc.charges ?? []);
    setDiscountType(doc.discount?.type ?? 'fixed');
    setDiscountValue(doc.discount?.value ?? 0);
  }, [selectedIntake, intakeId, isNew]);

  const doc = (!isNew && selectedIntake?.intakeId === intakeId ? selectedIntake : null) as
    | StockIntake
    | null;
  const readOnly = !!doc && doc.status !== 'draft';

  const activeIngredients = useMemo(
    () => ((ingredients ?? []) as StockItem[]).filter((i) => i.isActive !== false),
    [ingredients],
  );

  /** Only unit-mode products receive stock as whole units. */
  const unitProducts = useMemo(() => {
    const list = products?.products ?? [];
    return list.filter((p: any) => p.stockMode === 'unit');
  }, [products]);

  const productsById = useMemo(
    () => new Map<string, any>((products?.products ?? []).map((p: any) => [p.pid, p])),
    [products],
  );
  const ingredientsById = useMemo(
    () => new Map<string, any>(activeIngredients.map((i) => [i.stockId, i])),
    [activeIngredients],
  );

  /**
   * Choices for the searchable pickers, by name. A product a line already
   * names stays listed even if it has left unit mode since, so a saved or
   * posted document still shows what it received.
   */
  const productOptions = useMemo(() => {
    const byName = (a: {label: string}, b: {label: string}) => a.label.localeCompare(b.label);
    const listed = new Set(unitProducts.map((p: any) => p.pid));
    const extra = lines
      .map((l) => l.productId)
      .filter((pid): pid is string => !!pid && !listed.has(pid) && productsById.has(pid))
      .map((pid) => productsById.get(pid));
    return [...unitProducts, ...extra]
      .map((p: any) => ({value: p.pid, label: String(p.productName ?? '').trim()}))
      .sort(byName);
  }, [unitProducts, lines, productsById]);

  const ingredientOptions = useMemo(
    () =>
      activeIngredients
        .map((i) => ({value: i.stockId, label: `${i.itemName} (${i.metricValue})`}))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [activeIngredients],
  );

  /**
   * A new intake started from a product's or an ingredient's "Received" uses
   * its default supplier when nothing else chose one.
   */
  useEffect(() => {
    if (!isNew || supplierId || searchParams.get('supplierId')) return;
    const first = lines.find(isFilled);
    if (!first) return;
    const fallback = first.kind === 'product'
      ? productsById.get(first.productId!)?.defaultSupplierId
      : ingredientsById.get(first.stockId!)?.defaultSupplierId;
    if (fallback) setSupplierId(fallback);
    // Only when the masters arrive; never fight a user's own choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsById, ingredientsById]);

  /** Offer the supplier's last delivery whenever one is chosen on a draft. */
  useEffect(() => {
    setPrefillOffer(null);
    if (!shopId || !supplierId || (doc && doc.status !== 'draft')) return undefined;
    let live = true;
    StockIntakeRepository.last(shopId, supplierId)
      .then((ls) => live && setPrefillOffer(ls.length ? ls : null))
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // doc is derived from selectedIntake; its status is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, supplierId, selectedIntake?.status]);

  const totals = useMemo(
    () =>
      estimateTotals({
        lines,
        charges,
        discount: discountValue > 0 ? {type: discountType, value: discountValue} : null,
      }),
    [lines, charges, discountType, discountValue],
  );

  const unitFor = (line: IntakeLine): string =>
    unitLabel(
      line,
      productsById,
      (stockId) => activeIngredients.find((i) => i.stockId === stockId)?.metricValue ?? '',
    );

  const setLine = (lineId: string, patch: Partial<IntakeLine>) =>
    setLines((ls) => ls.map((l) => (l.lineId === lineId ? {...l, ...patch} : l)));

  /** Switching a line's kind clears the picker — the two id spaces are separate. */
  const switchKind = (lineId: string, kind: LineKind) =>
    setLine(lineId, {kind, productId: null, stockId: null, variantId: null});

  // A new line starts on the same type as the line above it: a delivery is
  // usually all products or all ingredients.
  const addLine = () => setLines((ls) => [...ls, blankLine(ls[ls.length - 1]?.kind)]);
  const removeLine = (lineId: string) =>
    setLines((ls) => (ls.length === 1 ? [blankLine()] : ls.filter((l) => l.lineId !== lineId)));

  /** Drop rows the user never filled in, so a trailing blank never blocks a save. */
  const meaningfulLines = () => lines.filter(isFilled);

  const buildInput = () => ({
    supplierId: supplierId || null,
    invoiceNo: invoiceNo || null,
    deliveryNoteNo: deliveryNoteNo.trim() || null,
    receivedBy: receivedBy.trim() || null,
    receivedAt: `${receivedAt}T00:00:00.000Z`,
    notes: notes || null,
    lines: meaningfulLines().map((l) => ({
      lineId: l.lineId,
      kind: l.kind,
      productId: l.kind === 'product' ? l.productId : null,
      stockId: l.kind === 'ingredient' ? l.stockId : null,
      variantId:
        l.kind === 'product' && variantsOf(productsById.get(l.productId ?? '')).length > 0
          ? l.variantId ?? null
          : null,
      qty: Number(l.qty) || 0,
      unitCost: Number(l.unitCost) || 0,
    })),
    charges: charges.filter((c) => c.label || c.amount),
    discount: discountValue > 0 ? {type: discountType, value: Number(discountValue)} : null,
  });

  const flash = (kind: 'ok' | 'err', text: string) => {
    setNotice({kind, text});
    setTimeout(() => setNotice(null), 5000);
  };

  /** Everything the server would refuse that the editor can see first. */
  const checkLines = (): boolean => {
    if (meaningfulLines().length === 0) {
      flash('err', 'Add at least one line before saving.');
      return false;
    }
    const found = variantProblems(meaningfulLines(), productsById);
    setProblems(found);
    if (found.length > 0) {
      flash('err', 'Choose a variant on every line marked below.');
      return false;
    }
    return true;
  };

  /**
   * Upload every file still waiting, now that the document exists. Each file
   * moves through uploading to stored, or to error with the reason; one
   * failure does not stop the others.
   */
  const uploadPending = async (id: string) => {
    const waiting = attachments.filter(
      (a): a is Exclude<AttachmentItem, {state: 'stored'}> => a.state === 'pending',
    );
    for (const item of waiting) {
      setAttachments((all) => all.map((a) => (a === item ? {...item, state: 'uploading'} : a)));
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await StockIntakeRepository.requestAttachmentUpload(shopId, id, item.file);
        const stored = res.intake?.attachments?.find(
          (x) => x.attachmentId === res.attachment.attachmentId,
        ) ?? res.attachment;
        setAttachments((all) =>
          all.map((a) =>
            (a as any).id === item.id ? {state: 'stored', attachment: stored} : a,
          ),
        );
      } catch (e: any) {
        setAttachments((all) =>
          all.map((a) =>
            (a as any).id === item.id
              ? {state: 'error', id: item.id, file: item.file, error: e?.message || 'Upload failed'}
              : a,
          ),
        );
      }
    }
  };

  const save = async (): Promise<string | null> => {
    setProblems([]);
    if (!checkLines()) return null;
    setBusy(true);
    try {
      const input = buildInput();
      if (isNew) {
        const created = await StockIntakeRepository.create(shopId, input);
        await uploadPending(created.intakeId);
        // Replace the URL rather than push, so Back does not land on /new.
        navigate(`/stock/intake/${created.intakeId}`, {replace: true});
        flash('ok', 'Draft saved.');
        return created.intakeId;
      }
      await StockIntakeRepository.update(shopId, intakeId!, input);
      await uploadPending(intakeId!);
      await fetchIntake(shopId, intakeId);
      flash('ok', 'Draft saved.');
      return intakeId!;
    } catch (e: any) {
      flash('err', e?.message || 'Could not save this draft.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  /** Save first: posting what is on screen, not what was last saved. */
  const saveAndPost = async () => {
    setConfirming(null);
    const id = await save();
    if (!id) return;
    setBusy(true);
    try {
      const res = await StockIntakeRepository.post(shopId, id);
      const levels: Record<string, string> = {};
      const byTarget = [
        ...(res.targets?.ingredients ?? []).map((t) => ({id: t.stockId, onHand: t.onHand})),
        ...(res.targets?.products ?? []).map((t) => ({id: t.productId, onHand: t.onHand})),
      ];
      for (const line of meaningfulLines()) {
        const target = line.kind === 'product' ? line.productId : line.stockId;
        const hit = byTarget.find((t) => t.id === target);
        if (hit) levels[line.lineId] = `${hit.onHand} ${unitFor(line)}`.trim();
      }
      setPostedLevels(levels);
      await fetchIntake(shopId, id);
      flash(
        'ok',
        res.alreadyPosted
          ? 'This intake was already posted; nothing changed.'
          : `Posted. ${res.events.syncedCount} stock event(s) written.`,
      );
    } catch (e: any) {
      // A 409 names the line that can no longer be received.
      flash('err', e?.message || 'Could not post this intake.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Receive now: create and post in one step (spec 5.1). A draft already on
   * the server is saved first, so what posts is what is on screen; a new
   * document is created by the receive call itself under `receiveId`, which
   * makes a retry safe. Files are uploaded after: the server can only take
   * an attachment for a document that exists, and it accepts them on a
   * posted one.
   */
  const receiveNow = async () => {
    setConfirming(null);
    setProblems([]);
    if (!checkLines()) return;
    let id = isNew ? receiveId : intakeId!;
    if (!isNew) {
      const saved = await save();
      if (!saved) return;
      id = saved;
    }
    setBusy(true);
    try {
      const res = await StockIntakeRepository.receiveNow(shopId, {...buildInput(), intakeId: id});
      const levels: Record<string, string> = {};
      for (const line of res.intake?.lines ?? []) {
        if (line.onHandAfter != null) {
          levels[line.lineId] = `${line.onHandAfter} ${unitFor(line)}`.trim();
        }
      }
      setPostedLevels(levels);
      await uploadPending(id);
      if (isNew) navigate(`/stock/intake/${id}`, {replace: true});
      await fetchIntake(shopId, id);
      flash(
        'ok',
        res.alreadyPosted
          ? 'This delivery was already received; nothing changed.'
          : `Received. ${res.events?.syncedCount ?? 0} stock event(s) written.`,
      );
    } catch (e: any) {
      flash('err', e?.message || 'Could not receive this delivery.');
    } finally {
      setBusy(false);
    }
  };

  /** Take the supplier's last delivery, asking before adding to typed lines. */
  const takePrefill = (ls: IntakeLine[]) => {
    const {lines: merged, needsConfirm} = applyPrefill(lines, ls, newLineId);
    if (needsConfirm) {
      setPrefillPending(merged);
      return;
    }
    setLines(merged.length ? merged : [blankLine()]);
    setPrefillOffer(null);
  };

  const addAttachments = (files: File[]) => {
    setAttachments((all) => [...all, ...toPendingItems(files, newLineId)]);
    // On a saved document the files go up straight away.
    if (!isNew && doc && doc.status !== 'void') {
      // Wait a tick for state to settle so uploadPending sees the new items.
      setTimeout(() => setUploadTick((t) => t + 1), 0);
    }
  };

  const [uploadTick, setUploadTick] = useState(0);
  useEffect(() => {
    if (uploadTick > 0 && !isNew && intakeId) {
      uploadPending(intakeId).then(() => fetchIntake(shopId, intakeId));
    }
    // Triggered by addAttachments only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadTick]);

  const removeAttachment = async (item: AttachmentItem) => {
    if (item.state !== 'stored') {
      setAttachments((all) => all.filter((a) => a !== item));
      return;
    }
    try {
      await StockIntakeRepository.deleteAttachment(shopId, intakeId!, item.attachment.attachmentId);
      setAttachments((all) => all.filter((a) => a !== item));
    } catch (e: any) {
      flash('err', e?.message || 'Could not remove that file.');
    }
  };

  const retryAttachment = (item: AttachmentItem) => {
    if (item.state !== 'error') return;
    setAttachments((all) => all.map((a) => (a === item ? {...item, state: 'pending', error: undefined} : a)));
    if (!isNew) setTimeout(() => setUploadTick((t) => t + 1), 0);
  };

  const doVoid = async () => {
    setBusy(true);
    try {
      await StockIntakeRepository.voidIntake(shopId, intakeId!, voidReason.trim());
      await fetchIntake(shopId, intakeId);
      setVoiding(false);
      setPostedLevels({});
      flash('ok', 'Intake voided. The stock it added has been reversed.');
    } catch (e: any) {
      flash('err', e?.message || 'Could not void this intake.');
    } finally {
      setBusy(false);
    }
  };

  const createSupplier = async (name: string): Promise<Supplier | null> => {
    try {
      const created = await SupplierRepository.create(shopId, {name});
      await fetchSuppliers(shopId);
      return created;
    } catch (e: any) {
      flash('err', e?.message || 'Could not add that supplier.');
      return null;
    }
  };

  const problemFor = (lineId: string) => problems.find((p) => p.lineId === lineId)?.error;

  return (
    // The page scrolls; its sections must never shrink to fit the screen —
    // flex would squeeze the lines card and clip rows and "+ Add line".
    <div className="h-screen flex flex-col w-full p-6 overflow-auto [&>*]:shrink-0">
      <div className="flex items-start justify-between gap-4 pb-5">
        <div>
          <button
            type="button"
            onClick={() => navigate('/stock')}
            className="text-sm text-text2 hover:text-brand"
          >
            ← Stock intakes
          </button>
          <h1 className="text-xl font-semibold text-text1 mt-1">
            {isNew ? 'New intake' : `Intake ${doc?.invoiceNo || intakeId}`}
          </h1>
          {doc && (
            <p className="text-sm text-text2 mt-1">
              {doc.status === 'draft' && 'Draft — nothing has moved yet.'}
              {doc.status === 'posted' &&
                `${postedViaLabel(doc.postedVia) ?? 'Posted'} ${doc.postedAt ? new Date(doc.postedAt).toLocaleString() : ''}. This document is read-only.`}
              {doc.status === 'void' &&
                `Voided${doc.voidReason ? `: ${doc.voidReason}` : ''}. Its stock has been reversed.`}
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {!readOnly && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={save}
                className="nl-btn nl-btn--secondary"
              >
                Save draft
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => checkLines() && setConfirming('post')}
                className="nl-btn nl-btn--soft"
              >
                Post
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => checkLines() && setConfirming('receive')}
                title="Create and post in one step"
                className="nl-btn nl-btn--primary"
              >
                Receive now
              </button>
            </>
          )}
          {doc?.status === 'posted' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setVoiding(true);
                setVoidReason('');
              }}
              className="nl-btn nl-btn--secondary"
            >
              Void
            </button>
          )}
          {doc && doc.status !== 'draft' && (
            <button
              type="button"
              onClick={() => window.print()}
              className="nl-btn nl-btn--secondary"
            >
              Print
            </button>
          )}
        </div>
      </div>

      {(notice || stockError) && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm ${
            notice?.kind === 'ok'
              ? 'border border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {notice?.text ?? stockError}
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-3 rounded-xl border border-borderc bg-surface p-5">
        <div>
          <span className={labelCls}>Supplier</span>
          <SupplierCombobox
            shopId={shopId}
            suppliers={(suppliers ?? []) as Supplier[]}
            value={supplierId}
            disabled={readOnly}
            onChange={setSupplierId}
            onCreate={createSupplier}
          />
        </div>
        <div>
          <span className={labelCls}>Received</span>
          <input
            type="date"
            className={inputCls}
            value={receivedAt}
            disabled={readOnly}
            onChange={(e) => setReceivedAt(e.target.value)}
          />
        </div>
        <div>
          <span className={labelCls}>Invoice number</span>
          <input
            className={inputCls}
            value={invoiceNo}
            disabled={readOnly}
            onChange={(e) => setInvoiceNo(e.target.value)}
          />
        </div>
        <div>
          <span className={labelCls}>Delivery note no.</span>
          <input
            className={inputCls}
            value={deliveryNoteNo}
            disabled={readOnly}
            placeholder="Supplier's GRN / delivery note"
            onChange={(e) => setDeliveryNoteNo(e.target.value)}
          />
        </div>
        <div>
          <span className={labelCls}>Received by</span>
          <input
            className={inputCls}
            value={receivedBy}
            disabled={readOnly}
            placeholder="Who signed for it"
            onChange={(e) => setReceivedBy(e.target.value)}
          />
        </div>
        <div>
          <span className={labelCls}>Notes</span>
          <input
            className={inputCls}
            value={notes}
            disabled={readOnly}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      {prefillOffer && !readOnly && (
        <div className="mt-3 flex items-center justify-between rounded-lg border border-brand/40 bg-brand-soft px-4 py-2.5 text-sm">
          <span className="text-text1">
            This supplier's last delivery had {prefillOffer.length} line
            {prefillOffer.length === 1 ? '' : 's'}.
          </span>
          <span className="flex gap-3">
            <button
              type="button"
              onClick={() => takePrefill(prefillOffer)}
              className="font-medium text-brand hover:opacity-80"
            >
              Prefill from last delivery
            </button>
            <button
              type="button"
              onClick={() => setPrefillOffer(null)}
              className="text-text2 hover:text-text1"
            >
              Dismiss
            </button>
          </span>
        </div>
      )}

      {/* ── Lines ──────────────────────────────────────────────────────── */}
      <div className="mt-4 overflow-hidden rounded-xl border border-borderc bg-surface">
        <table className="w-full text-sm">
          <thead className="bg-surface2 text-text2">
            <tr>
              <th className="px-4 py-3 text-left font-medium w-[190px]">Type</th>
              <th className="px-4 py-3 text-left font-medium">Item</th>
              <th className="px-4 py-3 text-right font-medium w-[150px]">Qty</th>
              <th className="px-4 py-3 text-right font-medium w-[170px]">Unit cost</th>
              <th className="px-4 py-3 text-right font-medium w-[150px]">Line total</th>
              <th className="w-[56px]" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const unit = unitFor(line);
              const err = problemFor(line.lineId);
              return (
                <tr key={line.lineId} className="border-t border-borderc align-top">
                  <td className="px-4 py-3">
                    <div
                      role="radiogroup"
                      aria-label="Line type"
                      className="inline-flex h-10 rounded-[10px] border border-borderc2 bg-surface2 p-1"
                    >
                      {(['product', 'ingredient'] as LineKind[]).map((k) => (
                        <button
                          key={k}
                          type="button"
                          role="radio"
                          aria-checked={line.kind === k}
                          disabled={readOnly}
                          onClick={() => switchKind(line.lineId, k)}
                          className={`rounded-[7px] px-3 text-xs font-medium transition-colors ${
                            line.kind === k
                              ? 'bg-surface text-brand shadow-sm'
                              : 'text-text2 hover:text-text1'
                          } disabled:cursor-not-allowed disabled:opacity-60`}
                        >
                          {k === 'ingredient' ? 'Ingredient' : 'Item'}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {line.kind === 'ingredient' ? (
                      <SearchSelect
                        aria-label="Ingredient"
                        placeholder="Search ingredients…"
                        value={line.stockId}
                        disabled={readOnly}
                        options={ingredientOptions}
                        onChange={(stockId) => setLine(line.lineId, {stockId})}
                      />
                    ) : (
                      <>
                        <SearchSelect
                          aria-label="Product"
                          placeholder="Search products…"
                          value={line.productId}
                          disabled={readOnly}
                          options={productOptions}
                          onChange={(productId) =>
                            setLine(line.lineId, {productId, variantId: null})
                          }
                          onCreate={(name) => setNewProduct({lineId: line.lineId, name})}
                          createLabel="Add new product"
                        />
                        {variantsOf(productsById.get(line.productId ?? '')).length > 0 && (
                          <div className="mt-1">
                            <SearchSelect
                              aria-label="Variant"
                              placeholder="Variant (required)…"
                              warn={!line.variantId && !readOnly}
                              value={line.variantId}
                              disabled={readOnly}
                              options={variantsOf(productsById.get(line.productId ?? '')).map((v) => ({
                                value: v.variantId,
                                label: v.label,
                              }))}
                              onChange={(variantId) => setLine(line.lineId, {variantId: variantId || null})}
                            />
                          </div>
                        )}
                        {readOnly && line.variantLabel && (
                          <p className="mt-1 text-[11px] text-text2">{line.variantLabel}</p>
                        )}
                      </>
                    )}
                    {line.kind === 'product' && unitProducts.length === 0 && (
                      <p className="mt-1 text-[11px] text-text2">
                        No products are in Count units mode yet.
                      </p>
                    )}
                    {err && <p className="mt-1 text-[11px] text-red-600">{err}</p>}
                    {postedLevels[line.lineId] && (
                      <p className="mt-1 text-[11px] text-emerald-700">
                        now on hand: {postedLevels[line.lineId]}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <AffixInput
                      type="number"
                      min="0"
                      step={line.kind === 'product' ? 1 : stepFor(unit)}
                      aria-label="Quantity"
                      suffix={unit}
                      value={line.qty || ''}
                      disabled={readOnly}
                      onChange={(e) => setLine(line.lineId, {qty: Number(e.target.value)})}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <AffixInput
                      type="number"
                      min="0"
                      step="0.01"
                      aria-label="Unit cost"
                      prefix={currency || undefined}
                      value={line.unitCost || ''}
                      disabled={readOnly}
                      onChange={(e) => setLine(line.lineId, {unitCost: Number(e.target.value)})}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex h-10 items-center justify-end font-medium tabular-nums text-text1">
                      {fmt((Number(line.qty) || 0) * (Number(line.unitCost) || 0), currency)}
                    </div>
                  </td>
                  <td className="py-3 pr-3">
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeLine(line.lineId)}
                        className="flex h-10 w-10 items-center justify-center rounded-[10px] text-text3 transition-colors hover:bg-red-50 hover:text-red-600"
                        title="Remove line"
                        aria-label="Remove line"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!readOnly && (
          <div className="border-t border-borderc px-4 py-3">
            <button
              type="button"
              onClick={addLine}
              className="text-sm font-medium text-brand hover:opacity-80"
            >
              + Add line
            </button>
          </div>
        )}
      </div>

      <AddProduct
        isOpen={!!newProduct}
        newDefaults={newProductDefaults}
        closeModal={() => setNewProduct(null)}
        onCreated={({pid}) => {
          if (newProduct) setLine(newProduct.lineId, {productId: pid, variantId: null});
        }}
      />

      <IntakeAttachments
        items={attachments}
        canAdd={doc?.status !== 'void'}
        canRemove={!doc || doc.status === 'draft'}
        onAdd={addAttachments}
        onRemove={removeAttachment}
        onRetry={retryAttachment}
      />

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <div className="mt-4 flex justify-end pb-8">
        <div className="nl-modal max-w-md p-5">
          <div className="flex justify-between py-1 text-sm">
            <span className="text-text2">Subtotal</span>
            <span className="tabular-nums text-text1">{fmt(totals.subtotal, currency)}</span>
          </div>

          <div className="mt-3 border-t border-borderc pt-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-text2">Charges</span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setCharges([...charges, {label: '', amount: 0}])}
                  className="text-xs font-medium text-brand hover:opacity-80"
                >
                  + Add charge
                </button>
              )}
            </div>
            {charges.map((c, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="mt-2 flex gap-2">
                <input
                  className={inputCls}
                  placeholder="Delivery"
                  value={c.label}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...charges];
                    next[i] = {...c, label: e.target.value};
                    setCharges(next);
                  }}
                />
                <input
                  type="number"
                  step="0.01"
                  className={`${inputCls} w-32 text-right tabular-nums`}
                  value={c.amount || ''}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...charges];
                    next[i] = {...c, amount: Number(e.target.value)};
                    setCharges(next);
                  }}
                />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => setCharges(charges.filter((_, j) => j !== i))}
                    className="text-text2 hover:text-red-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            {charges.length > 0 && (
              <div className="mt-2 flex justify-between text-sm">
                <span className="text-text2">Charges total</span>
                <span className="tabular-nums text-text1">{fmt(totals.chargesTotal, currency)}</span>
              </div>
            )}
          </div>

          <div className="mt-3 border-t border-borderc pt-3">
            <span className="text-xs font-medium text-text2">Discount</span>
            <div className="mt-2 flex gap-2">
              <select
                className={`${selectCls} w-32`}
                value={discountType}
                disabled={readOnly}
                onChange={(e) => setDiscountType(e.target.value as 'fixed' | 'percent')}
              >
                <option value="fixed">Amount</option>
                <option value="percent">Percent</option>
              </select>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`${inputCls} text-right tabular-nums`}
                value={discountValue || ''}
                disabled={readOnly}
                onChange={(e) => setDiscountValue(Number(e.target.value))}
              />
            </div>
            {totals.discountTotal > 0 && (
              <div className="mt-2 flex justify-between text-sm">
                <span className="text-text2">
                  Discount{discountType === 'percent' ? ` (${discountValue}%)` : ''}
                </span>
                <span className="tabular-nums text-text1">
                  −{fmt(totals.discountTotal, currency)}
                </span>
              </div>
            )}
          </div>

          <div className="mt-3 flex justify-between border-t border-borderc pt-3">
            <span className="font-semibold text-text1">Total</span>
            <span className="tabular-nums font-semibold text-text1">
              {fmt(totals.total, currency)}
            </span>
          </div>
          {!readOnly && (
            <p className="mt-2 text-[11px] text-text2">
              Totals are recalculated by the server when this saves.
            </p>
          )}
        </div>
      </div>

      {confirming && (
        <div className="nl-backdrop">
          <div className="nl-modal max-w-lg p-5">
            <h2 className="text-base font-semibold text-text1">
              {confirming === 'receive' ? 'Receive this delivery now?' : 'Post this intake?'}
            </h2>
            <p className="mt-2 text-sm text-text2">
              {confirming === 'receive'
                ? 'The document is saved and posted in one step. Stock moves straight away and the document becomes read-only.'
                : 'Stock moves straight away and the document becomes read-only. A mistake is corrected by voiding it.'}
            </p>
            <table className="mt-4 w-full text-sm">
              <thead className="text-text2">
                <tr>
                  <th className="py-1 text-left font-medium">Item</th>
                  <th className="py-1 text-right font-medium">Qty</th>
                  <th className="py-1 text-right font-medium">On hand after</th>
                </tr>
              </thead>
              <tbody>
                {meaningfulLines().map((line) => {
                  const name =
                    line.kind === 'product'
                      ? productsById.get(line.productId ?? '')?.productName
                      : ingredientsById.get(line.stockId ?? '')?.itemName;
                  const projection = projectedOnHand(line, productsById, ingredientsById);
                  return (
                    <tr key={line.lineId} className="border-t border-borderc">
                      <td className="py-1.5 text-text1">
                        {name ?? line.name ?? '—'}
                        {line.variantId && (
                          <span className="text-text2"> · {unitFor(line)}</span>
                        )}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-text1">{line.qty}</td>
                      <td className="py-1.5 text-right tabular-nums text-text1">
                        {projection ? `${projection.now} → ${projection.after}` : 'not counted yet'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(null)}
                className="nl-btn nl-btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={confirming === 'receive' ? receiveNow : saveAndPost}
                className="nl-btn nl-btn--primary"
              >
                {confirming === 'receive' ? 'Receive now' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}

      {prefillPending && (
        <div className="nl-backdrop">
          <div className="nl-modal max-w-md p-5">
            <h2 className="text-base font-semibold text-text1">Add the last delivery's lines?</h2>
            <p className="mt-2 text-sm text-text2">
              You have already entered lines. The last delivery's lines will be
              added after them; nothing you typed is replaced.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPrefillPending(null)}
                className="nl-btn nl-btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setLines(prefillPending);
                  setPrefillPending(null);
                  setPrefillOffer(null);
                }}
                className="nl-btn nl-btn--primary"
              >
                Add lines
              </button>
            </div>
          </div>
        </div>
      )}

      {voiding && (
        <div className="nl-backdrop">
          <div className="nl-modal max-w-md p-5">
            <h2 className="text-base font-semibold text-text1">Void this intake?</h2>
            <p className="mt-2 text-sm text-text2">
              A reversal is appended for every line, so the stock this added
              comes back off. The document stays, marked Void.
            </p>
            <div className="mt-4">
              <span className={labelCls}>Reason *</span>
              <input
                className={inputCls}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Wrong delivery, returned to supplier…"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setVoiding(false)}
                className="nl-btn nl-btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!voidReason.trim() || busy}
                onClick={doVoid}
                className="nl-btn nl-btn--primary"
              >
                Void intake
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default connect(
  (state: any) => ({
    selectedIntake: state.stock.get('selectedIntake'),
    suppliers: state.stock.get('suppliers'),
    ingredients: state.stock.get('ingredients'),
    stockError: state.stock.get('stockError'),
    products: state.product.get('products'),
    selectedShop: state.dashboard.get('selectedShop'),
  }),
  {
    fetchIntake: Actions.stock.fetchIntake,
    fetchSuppliers: Actions.stock.fetchSuppliers,
    fetchIngredients: Actions.stock.fetchIngredients,
    fetchProducts: Actions.product.fetchProducts,
    clearSelectedIntake: Actions.stock.clearSelectedIntake,
  },
)(StockIntakeEditorView);
